/**
 * Serial-date arithmetic shared by the date functions and by `TEXT`.
 *
 * A date is a number: serial 1 is 1900-01-01, the fractional part is the time of
 * day, and the Format_Manager decides how it is displayed. That is the same
 * model as `CellValue`, which stores dates, times, percentages, and currency as
 * plain numbers plus a number format.
 *
 * Excel's 1900 leap-year bug (serial 60 = a non-existent 29 February 1900) is
 * deliberately *not* reproduced: the mapping here is a straight offset from the
 * proleptic Gregorian calendar, so every serial round-trips through
 * {@link partsFromSerial} and {@link serialFromParts}. The consequence is that
 * dates before 1 March 1900 differ from Excel by one day; dates after it — every
 * date a user is likely to hold — agree exactly.
 *
 * All arithmetic is UTC, so results never depend on the machine's time zone.
 */

export const MS_PER_DAY = 86_400_000;

/** Days between the serial epoch (1899-12-30) and the Unix epoch (1970-01-01). */
export const SERIAL_EPOCH_OFFSET = 25_569;

/** Calendar and clock components of a serial date. */
export interface DateParts {
  year: number;
  /** 1-based month. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday, matching `Date.prototype.getUTCDay`. */
  weekday: number;
}

/** Serial date for a UTC timestamp in milliseconds. */
export function serialFromMs(ms: number): number {
  return ms / MS_PER_DAY + SERIAL_EPOCH_OFFSET;
}

/** UTC timestamp in milliseconds for a serial date. */
export function msFromSerial(serial: number): number {
  return (serial - SERIAL_EPOCH_OFFSET) * MS_PER_DAY;
}

/** Splits a serial date into calendar and clock components. */
export function partsFromSerial(serial: number): DateParts {
  const days = Math.floor(serial);
  const fraction = serial - days;
  const dayMs = Math.round(fraction * MS_PER_DAY);
  const d = new Date(msFromSerial(days) + dayMs);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    weekday: d.getUTCDay(),
  };
}

/**
 * Serial date for a calendar date. Month and day overflow roll over the way
 * Excel's `DATE` does (`DATE(2024,13,1)` is January 2025), and a two-digit year
 * below 1900 is offset into the 20th century, also matching Excel.
 */
export function serialFromParts(year: number, month: number, day: number, dayFraction = 0): number {
  const y = year >= 0 && year <= 1899 ? year + 1900 : year;
  const ms = Date.UTC(y, month - 1, day);
  return serialFromMs(ms) + dayFraction;
}

/** Whole days in the month containing `serial`, after adding `months`. */
export function endOfMonthSerial(serial: number, months: number): number {
  const parts = partsFromSerial(serial);
  // Day 0 of the following month is the last day of the wanted month.
  return serialFromParts(parts.year, parts.month + months + 1, 0);
}

/** Whole months between two serial dates, ignoring the day of month. */
export function wholeMonthsBetween(from: DateParts, to: DateParts): number {
  const months = (to.year - from.year) * 12 + (to.month - from.month);
  return to.day < from.day ? months - 1 : months;
}
